import React from 'react';
import { Select as MuiSelect, MenuItem, Checkbox, styled } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Colors } from 'library';

type MultiSelectTone = 'primary' | 'muted' | 'error';

interface MultiSelectOption {
    key: string;
    value: string;
}

interface MultiSelectProps {
    value: string[];
    onChange: (value: string[]) => void;
    options: MultiSelectOption[];
    placeholder?: string;
    allLabel?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    tone?: MultiSelectTone;
}

const TONE_BG: Record<MultiSelectTone, string> = {
    primary: Colors.white,
    muted: Colors.faintGrey,
    error: Colors.lightRed,
};

const TONE_BORDER: Record<MultiSelectTone, string> = {
    primary: Colors.lightGrey,
    muted: Colors.lightGrey,
    error: Colors.mediumRed,
};

const TONE_FOCUS_BORDER: Record<MultiSelectTone, string> = {
    primary: Colors.mediumGrey,
    muted: Colors.mediumGrey,
    error: Colors.mediumRed,
};

export function MultiSelect({
    value,
    onChange,
    options,
    placeholder,
    allLabel = 'All',
    disabled,
    fullWidth,
    tone = 'muted',
}: MultiSelectProps) {
    return (
        <StyledSelect
            multiple
            value={value}
            onChange={(e) => {
                const selected = e.target.value;
                onChange(typeof selected === 'string' ? selected.split(',') : (selected as string[]));
            }}
            disabled={disabled}
            fullWidth={fullWidth}
            displayEmpty
            IconComponent={StyledExpandMore}
            $tone={tone}
            MenuProps={{
                PaperProps: {
                    sx: {
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginTop: '4px',
                    },
                },
            }}
            renderValue={(selected) => {
                const values = selected as string[];
                if (!values.length) {
                    return placeholder ? <Placeholder>{placeholder}</Placeholder> : allLabel;
                }
                return values.map((key) => options.find((o) => o.key === key)?.value ?? key).join(', ');
            }}
        >
            {options.map((opt) => (
                <StyledMenuItem key={opt.key} value={opt.key}>
                    <StyledCheckbox checked={value.includes(opt.key)} size="small" />
                    {opt.value}
                </StyledMenuItem>
            ))}
        </StyledSelect>
    );
}

const StyledExpandMore = styled(ExpandMore)({
    fontSize: 20,
    color: Colors.mediumGrey,
});

const StyledSelect = styled(MuiSelect, { shouldForwardProp: (p) => p !== '$tone' })<{ $tone: MultiSelectTone }>(
    ({ $tone }) => ({
        fontSize: 14,
        color: Colors.darkNavy,
        backgroundColor: TONE_BG[$tone],
        borderRadius: 10,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_BORDER[$tone],
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_BORDER[$tone],
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_FOCUS_BORDER[$tone],
            borderWidth: 1,
        },
        '&.Mui-disabled': {
            backgroundColor: Colors.fill,
            color: Colors.mediumGrey,
        },
        '& .MuiSelect-select': {
            padding: '10px 14px',
        },
    }),
);

const StyledMenuItem = styled(MenuItem)({
    fontSize: 14,
    padding: '3px 14px 3px 6px',
    margin: '0 8px',
    minHeight: 'unset',
    borderRadius: 8,
    '&.Mui-selected': {
        backgroundColor: 'transparent',
        color: Colors.darkNavy,
    },
    '&.Mui-selected:hover': {
        backgroundColor: Colors.faintGrey,
    },
    '&:hover': {
        backgroundColor: Colors.faintGrey,
    },
});

const StyledCheckbox = styled(Checkbox)({
    padding: '4px 8px 4px 4px',
    color: Colors.lightGrey,
    '&.Mui-checked': {
        color: Colors.turquoise,
    },
});

const Placeholder = styled('span')({
    color: Colors.lightGrey,
});
